import { useCallback } from 'react';
import type {
	ChildResult,
	Command,
	CommandSections,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { QuickSearchScope } from '../../types.tsx';
import { QUICK_SEARCH_PRIORITY } from '../constants.tsx';
import type { GetQuickSearchChildResultProps, UseGetChildResult } from '../types.tsx';
import {
	getQuickSearchBoardScope,
	getQuickSearchFilterScope,
	getQuickSearchIssueScope,
	getQuickSearchPlanScope,
	getQuickSearchProjectScope,
} from '../utils.tsx';
import { useGetViewAllCommand } from '../view-all/index.tsx';
import { ViewAllType, getViewAllUrl } from '../view-all/utils.tsx';
import { messages } from './messages.tsx';

const SECTION_ID = 'recommended';

export const useGetRecommendedChildResult: UseGetChildResult = () => {
	const { getViewAllCommand } = useGetViewAllCommand();

	const { formatMessage } = useIntl();

	const getChildResult = useCallback(
		({ search, quickSearchResponseData, error }: GetQuickSearchChildResultProps): ChildResult => {
			const isSearching = Boolean(search);

			const sections: CommandSections = {
				[SECTION_ID]: {
					name: formatMessage(messages.recommendedSection),
					priority: QUICK_SEARCH_PRIORITY.RECOMMENDED,
				},
			};

			const commands: Command[] = [];
			const issueScope = getQuickSearchIssueScope(quickSearchResponseData);
			const projectScope = getQuickSearchProjectScope(quickSearchResponseData);
			const boardScope = getQuickSearchBoardScope(quickSearchResponseData);
			const filterScope = getQuickSearchFilterScope(quickSearchResponseData);
			const planScope = getQuickSearchPlanScope(quickSearchResponseData);

			const showRecommended = (scope?: QuickSearchScope) =>
				!scope || scope.error || error || (isSearching && !scope?.results?.length);

			if (showRecommended(issueScope)) {
				commands.push(
					getViewAllCommand({
						id: 'recommended-issues',
						section: SECTION_ID,
						name: formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.viewAllIssuesIssueTermRefresh
								: messages.viewAllIssues,
						),
						keywords: search,
						isRecommended: true,
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						url: getViewAllUrl(ViewAllType.ISSUE)!,
						priority: QUICK_SEARCH_PRIORITY.ISSUES,
						analyticsPayload: { action: 'viewAllIssues' },
					}),
				);
			}

			if (showRecommended(boardScope)) {
				commands.push(
					getViewAllCommand({
						id: 'recommended-boards',
						section: SECTION_ID,
						name: formatMessage(messages.viewAllBoards),
						keywords: search,
						isRecommended: true,
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						url: getViewAllUrl(ViewAllType.BOARD)!,
						priority: QUICK_SEARCH_PRIORITY.BOARDS,
						analyticsPayload: { action: 'viewAllBoards' },
					}),
				);
			}

			if (showRecommended(projectScope)) {
				commands.push(
					getViewAllCommand({
						id: 'recommended-projects',
						section: SECTION_ID,
						name: formatMessage(messages.viewAllProjects),
						keywords: search,
						isRecommended: true,
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						url: getViewAllUrl(ViewAllType.PROJECT)!,
						priority: QUICK_SEARCH_PRIORITY.PROJECTS,
						analyticsPayload: { action: 'viewAllProjects' },
					}),
				);
			}

			if (showRecommended(filterScope)) {
				commands.push(
					getViewAllCommand({
						id: 'recommended-filters',
						section: SECTION_ID,
						name: formatMessage(messages.viewAllFilters),
						keywords: search,
						isRecommended: true,
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						url: getViewAllUrl(ViewAllType.FILTER)!,
						priority: QUICK_SEARCH_PRIORITY.FILTERS,
						analyticsPayload: { action: 'viewAllFilters' },
					}),
				);
			}

			if (showRecommended(planScope)) {
				commands.push(
					getViewAllCommand({
						id: 'recommended-plans',
						section: SECTION_ID,
						name: formatMessage(messages.viewAllPlans),
						keywords: search,
						isRecommended: true,
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						url: getViewAllUrl(ViewAllType.PLAN)!,
						priority: QUICK_SEARCH_PRIORITY.PLANS,
						analyticsPayload: { action: 'viewAllPlans' },
					}),
				);
			}

			return {
				sections,
				commands,
			};
		},
		[formatMessage, getViewAllCommand],
	);

	return { getChildResult };
};
